import type {
  AccountInfo,
  AuthenticationResult,
  PopupRequest,
  PublicClientApplication,
} from '@azure/msal-browser';

import { MSAL_CONFIG } from '@/constants';
import { accountWithMsalIdToken } from '@/helper/auth/oauth-account';

/**
 * Web authentication strategy using popup flow
 */
export class WebAuthStrategy {
  constructor(
    private setUser: (
      account: AccountInfo,
      accessToken?: string,
      expiresOn?: Date,
    ) => void,
    private initLocaleStorage: () => Promise<void>,
    private redirectAfterLogin: () => Promise<void>,
  ) {}

  /** Native needs redirectUri/msalConfig in the request; popup uses MSAL ctor config only. */
  async login(
    msalInstance: PublicClientApplication,
    _getRedirectUri: () => string,
    _msalConfig: { auth: { authority: string; clientId: string } },
  ): Promise<void> {
    const loginRequest: PopupRequest = {
      scopes: MSAL_CONFIG.SCOPES.FULL.split(' '),
    };

    try {
      const response: AuthenticationResult =
        await msalInstance.loginPopup(loginRequest);
      console.log('Login response:', JSON.stringify(response));
      // accessToken → user.access_token (Graph). user.token for API comes from id_token on account.
      this.setUser(
        accountWithMsalIdToken(response),
        response.accessToken,
        response.expiresOn || undefined,
      );
      await this.initLocaleStorage();
      this.redirectAfterLogin();
    } catch (error: any) {
      // Handle MSAL "interaction in progress" error
      const errorMessage = error?.message || String(error);
      const errorCode = error?.errorCode;
      
      if (
        errorCode === 'interaction_in_progress' ||
        errorMessage.toLowerCase().includes('interaction in progress') ||
        errorMessage.toLowerCase().includes('interaction is currently in progress')
      ) {
        // Re-throw with a more user-friendly message
        // The parent will handle clearing the stuck state
        throw error;
      }
      // Re-throw other errors as-is
      throw error;
    }
  }

  async logout(
    msalInstance: PublicClientApplication,
    _getRedirectUri: () => string,
  ): Promise<void> {
    await msalInstance.logoutPopup();
  }
}
